// The Dal Segno Machine — what happens to notation if repeat counts may
// REFERENCE each other. Standard notation's control flow (repeats, voltas,
// D.C./D.S., Nx counts) only ever compares a pass counter against constants
// written in the score, which is why every chart flattens to a finite
// performance (flattenChart is a total function). Add ONE construct —
// "repeat this section as many times as A was just repeated, ±k" — and
// rehearsal letters become registers, repeat marks become read/write
// arithmetic, and a conditional D.S. ("se A=0") becomes branch-if-zero.
// That triple is exactly a Minsky counter machine (1967): sheet music goes
// Turing complete, and "does this chart end?" becomes the halting problem.
// This module is the pure semantics: a tiny score-program model, a
// fuel-bounded performer, and the preset pieces the Lab exhibit plays.

/** A repeat count: a written constant, the soloist's choice, or ×(A±k). */
export type CountExpr =
  | { kind: 'const'; times: number }
  | { kind: 'input'; slot?: number }
  | { kind: 'ref'; letter: string; delta: number };

/** A conditional jump — "D.S. se A=0", "al Coda se B>0", or unconditional. */
export interface Jump {
  when?: { letter: string; test: 'zero' | 'pos' };
  to: number;
}

/** One line of the score-program. */
export type SegnoLine =
  | {
      kind: 'play';
      /** Rehearsal letter — the register this section's count is written to. */
      letter: string;
      expr: CountExpr;
      /** The section's riff, one strum per pass (midi notes). */
      midi: number[];
      jumps?: Jump[];
      comment?: string;
    }
  | { kind: 'mark'; jumps?: Jump[]; fine?: boolean; comment?: string };

export interface SegnoProgram {
  lines: SegnoLine[];
}

/** One performed event: a bar of a section, a tacet skip, or a mark visit. */
export interface TraceEvent {
  kind: 'bar' | 'tacet' | 'mark' | 'fine';
  line: number;
  letter?: string;
  /** 1-based pass through the section (bars only). */
  pass?: number;
  /** The evaluated count for this traversal of the section. */
  count?: number;
  /** Committed register state AFTER this event. */
  registers: Record<string, number>;
}

export interface RunResult {
  /** True when the performance reached Fine / fell off the end. */
  halted: boolean;
  trace: TraceEvent[];
  /** Sounding bars played (tacets and marks are free). */
  bars: number;
  registers: Record<string, number>;
}

export function evalCount(expr: CountExpr, registers: Record<string, number>, inputs: number[]) {
  switch (expr.kind) {
    case 'const':
      return Math.max(0, expr.times);
    case 'input':
      return Math.max(0, Math.floor(inputs[expr.slot ?? 0] ?? 0));
    case 'ref':
      return Math.max(0, (registers[expr.letter] ?? 0) + expr.delta);
  }
}

/** Render a count expression the way the score would print it. */
export function countLabel(expr: CountExpr): string {
  switch (expr.kind) {
    case 'const':
      return `×${expr.times}`;
    case 'input':
      // Open vamps — the soloists' choices are the program's inputs.
      return `×${'nmk'[expr.slot ?? 0] ?? 'n'}`;
    case 'ref': {
      const d = expr.delta;
      const tail = d === 0 ? '' : d > 0 ? `+${d}` : `−${-d}`;
      return `×(${expr.letter}${tail})`;
    }
  }
}

/** Render a jump the way the score would print it (Italian, like all directions). */
export function jumpLabel(jump: Jump, lines: SegnoLine[]): string {
  const target = lines[jump.to];
  const where = target?.kind === 'mark' && target.fine ? 'al Fine' : `al №${jump.to + 1}`;
  if (!jump.when) return `D.S. ${where}`;
  const rel = jump.when.test === 'zero' ? '= 0' : '> 0';
  return `${where} se ${jump.when.letter} ${rel}`;
}

/**
 * Perform the score. `fuel` bounds the RUN, not the piece — with referential
 * repeats the halting problem is real, so the performer carries gas. Every
 * line execution costs at least one fuel step (so even mark-only loops
 * terminate); each sounding bar costs one.
 */
export function runSegnoMachine(
  program: SegnoProgram,
  opts: { input?: number; inputs?: number[]; fuel?: number } = {},
): RunResult {
  const inputs = opts.inputs ?? [opts.input ?? 0];
  const fuel = opts.fuel ?? 600;
  const registers: Record<string, number> = {};
  for (const line of program.lines) {
    if (line.kind === 'play') registers[line.letter] = registers[line.letter] ?? 0;
  }
  const trace: TraceEvent[] = [];
  let bars = 0;
  let steps = 0;
  let pc = 0;

  const takeJump = (jumps: Jump[] | undefined): number | null => {
    for (const j of jumps ?? []) {
      if (!j.when) return j.to;
      const v = registers[j.when.letter] ?? 0;
      if (j.when.test === 'zero' ? v === 0 : v > 0) return j.to;
    }
    return null;
  };

  while (pc >= 0 && pc < program.lines.length) {
    if (steps >= fuel) return { halted: false, trace, bars, registers: { ...registers } };
    const line = program.lines[pc];
    if (line.kind === 'mark') {
      steps += 1;
      if (line.fine) {
        trace.push({ kind: 'fine', line: pc, registers: { ...registers } });
        return { halted: true, trace, bars, registers: { ...registers } };
      }
      trace.push({ kind: 'mark', line: pc, registers: { ...registers } });
      const to = takeJump(line.jumps);
      pc = to ?? pc + 1;
      continue;
    }
    // Play line: evaluate ONCE at entry (the performer reads the mark, then
    // counts), sound n bars, and commit the count to the letter's register
    // when the section completes — ×(A−1) reads the OLD value of A.
    const n = evalCount(line.expr, registers, inputs);
    if (n === 0) {
      steps += 1;
      registers[line.letter] = 0;
      trace.push({
        kind: 'tacet',
        line: pc,
        letter: line.letter,
        count: 0,
        registers: { ...registers },
      });
    } else {
      for (let pass = 1; pass <= n; pass++) {
        if (steps >= fuel) return { halted: false, trace, bars, registers: { ...registers } };
        steps += 1;
        bars += 1;
        if (pass === n) registers[line.letter] = n;
        trace.push({
          kind: 'bar',
          line: pc,
          letter: line.letter,
          pass,
          count: n,
          registers: { ...registers },
        });
      }
    }
    const to = takeJump(line.jumps);
    pc = to ?? pc + 1;
  }
  trace.push({ kind: 'fine', line: program.lines.length - 1, registers: { ...registers } });
  return { halted: true, trace, bars, registers: { ...registers } };
}

// ---------------------------------------------------------------------------
// The preset pieces. Riffs are simple diatonic figures so the counting is
// audible: each letter keeps one recognisable sound.

const RIFF: Record<string, number[]> = {
  A: [48, 55, 60], // C bass + fifth + octave — the vamp
  B: [64, 67], // the answer, up high
  C: [52, 59, 62], // the accumulating third voice
};

export interface SegnoPreset {
  id: string;
  title: string;
  description: string;
  program: SegnoProgram;
  /** Present when the piece takes soloists' inputs (open vamps), in slot order. */
  inputs?: { label: string; min: number; max: number; default: number }[];
  /** What to read off the final registers, for the verdict line. */
  verdict: (result: RunResult, inputs: number[]) => string;
}

const play = (letter: string, expr: CountExpr, jumps?: Jump[], comment?: string): SegnoLine => ({
  kind: 'play',
  letter,
  expr,
  midi: RIFF[letter] ?? [60],
  jumps,
  comment,
});
const mark = (jumps?: Jump[], comment?: string): SegnoLine => ({ kind: 'mark', jumps, comment });
const fine = (comment?: string): SegnoLine => ({ kind: 'mark', fine: true, comment });

export const SEGNO_PRESETS: SegnoPreset[] = [
  {
    id: 'echo',
    title: 'The Echo',
    description:
      'Copies a register. A is played n times (the input), then B is played the same number of times.',
    program: {
      lines: [
        play('A', { kind: 'input' }, undefined, 'the call — soloist chooses n'),
        play('B', { kind: 'ref', letter: 'A', delta: 0 }, undefined, 'the response — exactly n'),
        fine(),
      ],
    },
    inputs: [{ label: 'Soloist vamps', min: 0, max: 12, default: 4 }],
    verdict: (r, [n]) => `B played ${r.registers.B} times, the same as A (${n}).`,
  },
  {
    id: 'countdown',
    title: 'The Countdown',
    description:
      'A loop: while A > 0, play B once and play A one time fewer. B is played n times, in O(n²) bars.',
    program: {
      lines: [
        play('A', { kind: 'input' }, undefined, 'load the counter'),
        mark([{ when: { letter: 'A', test: 'zero' }, to: 4 }], '𝄋 — al Fine se A = 0'),
        play('B', { kind: 'const', times: 1 }, undefined, 'the loop body'),
        play(
          'A',
          { kind: 'ref', letter: 'A', delta: -1 },
          [{ to: 1 }],
          'decrement: one fewer, D.S.',
        ),
        fine('⊕ Fine'),
      ],
    },
    inputs: [{ label: 'Count down from', min: 0, max: 9, default: 5 }],
    verdict: (r, [n]) =>
      `B played ${r.trace.filter((e) => e.letter === 'B').length} times for n = ${n}, in ${r.bars} bars.`,
  },
  {
    id: 'sum',
    title: 'The Sum',
    description:
      'Adds two inputs a and b. C copies A, then C goes up by one and B down by one until B is zero. C ends at a + b.',
    program: {
      lines: [
        play('A', { kind: 'input', slot: 0 }, undefined, 'first addend — soloist one'),
        play('B', { kind: 'input', slot: 1 }, undefined, 'second addend — soloist two'),
        play('C', { kind: 'ref', letter: 'A', delta: 0 }, undefined, 'C := A'),
        mark([{ when: { letter: 'B', test: 'zero' }, to: 6 }], '𝄋 — al Fine se B = 0'),
        play('C', { kind: 'ref', letter: 'C', delta: 1 }, undefined, 'C grows…'),
        play('B', { kind: 'ref', letter: 'B', delta: -1 }, [{ to: 3 }], '…as B gives, D.S.'),
        fine('⊕ Fine — C holds the sum'),
      ],
    },
    inputs: [
      { label: 'a', min: 0, max: 8, default: 3 },
      { label: 'b', min: 0, max: 8, default: 4 },
    ],
    verdict: (r, [a, b]) =>
      `C ended at ${r.registers.C}: ${a} + ${b} = ${r.registers.C}, in ${r.bars} bars.`,
  },
  {
    id: 'ulam',
    title: "Ulam's Vamp",
    description:
      'The Collatz map. A holds n. An even n is halved; an odd n becomes 3n + 1. The score reaches Fine only when n is 1. Whether every n gets there is the Collatz conjecture, which is unproven.',
    program: {
      lines: [
        play('A', { kind: 'input' }, undefined, 'the soloist opens with n'),
        // loop top: test n = 1 (decrement, test zero, restore)
        play('A', { kind: 'ref', letter: 'A', delta: -1 }, undefined, 'probe: n − 1'),
        mark([{ when: { letter: 'A', test: 'zero' }, to: 15 }], 'al Fine se A = 0 (n was 1)'),
        play('A', { kind: 'ref', letter: 'A', delta: 1 }, undefined, 'restore n'),
        // parity: strip pairs of A into B
        mark([{ when: { letter: 'A', test: 'zero' }, to: 9 }], 'se A = 0: even — B holds n/2'),
        play('A', { kind: 'ref', letter: 'A', delta: -1 }),
        mark([{ when: { letter: 'A', test: 'zero' }, to: 11 }], 'se A = 0: odd — B holds ⌊n/2⌋'),
        play('A', { kind: 'ref', letter: 'A', delta: -1 }),
        play('B', { kind: 'ref', letter: 'B', delta: 1 }, [{ to: 4 }], 'one pair banked, D.S.'),
        // even: n := n/2
        play('A', { kind: 'ref', letter: 'B', delta: 0 }, undefined, 'A := B (halved)'),
        play('B', { kind: 'const', times: 0 }, [{ to: 1 }], 'tacet — reset B, D.S. al top'),
        // odd: n := 3n + 1 = 6·B + 4, paying six As per banked pair
        mark([{ when: { letter: 'B', test: 'zero' }, to: 14 }], 'se B = 0: pairs spent'),
        play('A', { kind: 'ref', letter: 'A', delta: 6 }, undefined, 'six As per pair…'),
        play('B', { kind: 'ref', letter: 'B', delta: -1 }, [{ to: 11 }], '…one pair spent, D.S.'),
        play('A', { kind: 'ref', letter: 'A', delta: 4 }, [{ to: 1 }], 'plus four, D.S. al top'),
        fine('⊕ Fine — the vamp reached 1'),
      ],
    },
    inputs: [{ label: 'Opening n', min: 1, max: 15, default: 6 }],
    verdict: (r, [n]) =>
      r.halted
        ? `n = ${n} reached Fine in ${r.bars} bars. No proof exists that every opening does.`
        : `n = ${n} had not reached 1 when the fuel ran out.`,
  },
  {
    id: 'perpetuo',
    title: 'Moto Perpetuo',
    description:
      'One bar with an unconditional D.S., so it never ends. With repeat counts that refer to other sections, no program can decide in general whether a score ends: the halting problem.',
    program: {
      lines: [play('A', { kind: 'const', times: 1 }, [{ to: 0 }], 'D.S. — sempre')],
    },
    verdict: (r) => `No Fine after ${r.bars} bars. Stopped when the fuel ran out.`,
  },
];

/** The Collatz map, for tests: the piece must agree with the mathematics. */
export function collatzSteps(n: number): number {
  let steps = 0;
  let v = n;
  while (v !== 1) {
    v = v % 2 === 0 ? v / 2 : 3 * v + 1;
    steps += 1;
    if (steps > 10_000) throw new Error('collatz reference runaway');
  }
  return steps;
}
